import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { engineerService } from '../../services/engineerService';
import { PageHeader, Button, Badge } from '../../components/ui';

const tone = (
  status: string,
  approval?: string | null,
): { tone: 'success' | 'warning' | 'neutral' | 'info' | 'danger'; label: string } => {
  if (approval === 'PENDING') return { tone: 'warning', label: 'Awaiting admin approval' };
  if (approval === 'REJECTED') return { tone: 'danger', label: 'Rejected by admin' };
  switch ((status || '').toUpperCase()) {
    case 'ACCEPTED':
      return { tone: 'success', label: 'Accepted' };
    case 'SENT':
      return { tone: 'info', label: 'Sent' };
    case 'DRAFT':
      return { tone: 'neutral', label: 'Draft' };
    case 'DECLINED':
      return { tone: 'danger', label: 'Declined' };
    default:
      return { tone: 'neutral', label: status || 'Unknown' };
  }
};

const EngineerQuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: quote, isLoading } = useQuery({
    queryKey: ['engineer', 'quote', id],
    queryFn: () => engineerService.getMyQuote(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Quote not found.
      </div>
    );
  }

  const q: any = quote;
  const info = tone(q.status, q.adminApprovalStatus);

  return (
    <div className="space-y-4">
      <PageHeader
        title={q.jobTitle || 'Quote'}
        subtitle={`${q.quoteNumber} · ${new Date(q.createdAt).toLocaleDateString()}`}
        actions={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
            onClick={() => navigate('/engineer/quotes')}
          >
            Back
          </Button>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between">
        <Badge tone={info.tone}>{info.label}</Badge>
        <div className="text-right">
          <div className="text-xs text-slate-500">Total</div>
          <div className="text-lg font-semibold text-slate-900">
            £{Number(q.totalAmount || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {q.description && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
            Scope
          </h3>
          <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">
            {q.description}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Line items</h3>
        </div>
        <ul className="divide-y divide-slate-100">
          {(q.items || []).map((it: any) => (
            <li key={it.id} className="px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-slate-900">{it.description}</div>
                <div className="text-xs text-slate-500">
                  {it.quantity} × £{Number(it.unitPrice).toFixed(2)}
                </div>
              </div>
              <div className="text-sm font-medium text-slate-900">
                £{Number(it.totalPrice).toFixed(2)}
              </div>
            </li>
          ))}
        </ul>
        <div className="px-4 py-3 border-t border-slate-100 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal</span>
            <span>£{Number(q.amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-600">
            <span>VAT</span>
            <span>£{Number(q.vatAmount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-200 pt-1">
            <span>Total</span>
            <span>£{Number(q.totalAmount || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {q.notes && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
            Notes
          </h3>
          <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">
            {q.notes}
          </p>
        </div>
      )}

      {q.signatureUrl && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
            Customer signature
          </h3>
          <img
            src={q.signatureUrl}
            alt="Customer signature"
            className="mt-2 h-28 object-contain"
          />
          {q.approvedByClientName && (
            <p className="text-xs text-slate-500 mt-1">
              Signed by {q.approvedByClientName}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EngineerQuoteDetail;
